import { CrossingGame } from '../../games/crossing/CrossingGame.js';
import { CrossingVortex } from '../../games/crossing/CrossingVortex.js';
import { CrossingRenderer } from '../../games/crossing/CrossingRenderer.js';
import { Grid } from '../../core/Grid.js';
import { Position, positionKey } from '../../core/GridGeometry.js';
import { RenderConfig } from '../../rendering/Renderer.js';
import { TutorialGameAdapter } from '../TutorialEngine.js';

// ── Crossing-specific types ───────────────────────────────────────

export interface CrossingRenderOptions {
    // Placeholder — CrossingRenderer is fully game-state driven
}

// ── Adapter ─────────────────────────────────────────────────────

export class CrossingTutorialAdapter implements TutorialGameAdapter {
    private game: CrossingGame;
    private renderer: CrossingRenderer;
    private grid: Grid;

    constructor(canvas: HTMLCanvasElement, grid: Grid) {
        this.grid = grid;
        this.game = new CrossingGame(grid);
        this.renderer = new CrossingRenderer(canvas, grid);
        // Static scenes: no tweening, so the renderer never self-re-renders and
        // wipes the engine-drawn annotations.
        this.renderer.setSelfAnimation(false);
    }

    applyState(state: unknown): void {
        const raw = state as string;
        this.game.reset();

        if (!raw) return;

        // Parse "pairString+click1,click2" notation
        // Left of '+': standard pair string for deserialize()
        // Right of '+': comma-separated positions to simulate via handleCellClick()
        const plusIndex = raw.indexOf('+');
        let pairPart: string;
        let clickPart: string;

        if (plusIndex >= 0) {
            pairPart = raw.substring(0, plusIndex);
            clickPart = raw.substring(plusIndex + 1);
        } else {
            pairPart = raw;
            clickPart = '';
        }

        if (pairPart) {
            this.game.deserialize(pairPart);
        }

        if (clickPart) {
            for (const posStr of clickPart.split(',')) {
                const trimmed = posStr.trim();
                if (!trimmed) continue;
                const pos = this.grid.serializer.deserializePosition(trimmed);
                this.game.handleCellClick(pos);
            }
        }
    }

    render(_options: unknown): void {
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createCrossingTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    return new CrossingTutorialAdapter(canvas, grid);
}

// ── Vortex state ─────────────────────────────────────────────────

export interface CrossingVortexTutorialState {
    crossing: string;
    gateMode?: boolean;
    /**
     * Extra gate pairs (cell strings, e.g. ['B5', 'D5']) merged on top of the
     * adapter's default teleport pair for this scene only — used by the
     * activation scene to show a second, blocked (amber) pair. Omit otherwise.
     */
    extraGates?: Array<[string, string]>;
}

/** Parse a cell string like "B5" into a Position ({ row: 5, col: 1 }). */
function parseCellString(s: string): Position {
    return { col: s.charCodeAt(0) - 65, row: parseInt(s.slice(1), 10) };
}


// ── Vortex Adapter ───────────────────────────────────────────────

export class CrossingVortexTutorialAdapter implements TutorialGameAdapter {
    private game: CrossingVortex;
    private renderer: CrossingRenderer;
    private grid: Grid;
    private gatePairs: Array<[Position, Position]>;
    private allGates: Set<string>;
    private currentGateMode: boolean = false;

    constructor(
        canvas: HTMLCanvasElement,
        grid: Grid,
        gatePairs: Array<[Position, Position]>,
    ) {
        this.grid = grid;
        this.game = new CrossingVortex(grid);
        this.renderer = new CrossingRenderer(canvas, grid);
        this.renderer.setSelfAnimation(false);   // static scenes — see the Base adapter
        this.gatePairs = gatePairs;
        this.allGates = new Set<string>();
        for (const [a, b] of gatePairs) {
            this.allGates.add(positionKey(a));
            this.allGates.add(positionKey(b));
        }
    }

    applyState(state: unknown): void {
        const s = state as CrossingVortexTutorialState | null;
        this.game.reset();
        const pairs = [...this.gatePairs];
        for (const [a, b] of s?.extraGates ?? []) {
            pairs.push([parseCellString(a), parseCellString(b)]);
        }
        const allGates = new Set<string>();
        for (const [a, b] of pairs) {
            allGates.add(positionKey(a));
            allGates.add(positionKey(b));
        }
        this.game.setVortexData(pairs, allGates);

        if (!s) return;

        // Parse "pairString+click1,click2" notation from crossing field
        const raw = s.crossing;
        if (raw) {
            const plusIndex = raw.indexOf('+');
            let pairPart: string;
            let clickPart: string;

            if (plusIndex >= 0) {
                pairPart = raw.substring(0, plusIndex);
                clickPart = raw.substring(plusIndex + 1);
            } else {
                pairPart = raw;
                clickPart = '';
            }

            if (pairPart) {
                this.game.deserialize(pairPart);
            }

            if (clickPart) {
                for (const posStr of clickPart.split(',')) {
                    const trimmed = posStr.trim();
                    if (!trimmed) continue;
                    const pos = this.grid.serializer.deserializePosition(trimmed);
                    this.game.handleCellClick(pos);
                }
            }
        }

        this.currentGateMode = s.gateMode ?? false;
    }

    render(_options: unknown): void {
        this.renderer.setGateMode(this.currentGateMode);
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createCrossingVortexTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    // Gate pair for the vortex crossing tutorial: E1 ↔ D4
    const gatePairs: Array<[Position, Position]> = [
        [{ row: 1, col: 4 }, { row: 4, col: 3 }],
    ];
    return new CrossingVortexTutorialAdapter(canvas, grid, gatePairs);
}
